import JSEncrypt from 'jsencrypt';
import { getPublicKey } from '@/api/user';

const ENCRYPT_FAIL_MSG = '安全组件初始化失败，请刷新重试';

let cachedPublicKey = null;
let publicKeyPromise = null;

/**
 * 获取 RSA 公钥（会话级内存缓存）
 */
export async function fetchPublicKey() {
  if (cachedPublicKey) {
    return cachedPublicKey;
  }
  if (publicKeyPromise) {
    return publicKeyPromise;
  }
  publicKeyPromise = getPublicKey()
    .then((res) => {
      const key = res?.public_key || res?.publicKey || res?.key || res?.data?.public_key;
      if (!key) {
        throw new Error(ENCRYPT_FAIL_MSG);
      }
      cachedPublicKey = key;
      return cachedPublicKey;
    })
    .finally(() => {
      publicKeyPromise = null;
    });
  return publicKeyPromise;
}

/**
 * 清除公钥缓存（如需要强制刷新）
 */
export function clearPublicKeyCache() {
  cachedPublicKey = null;
  publicKeyPromise = null;
}

/**
 * 使用 PKCS#1 加密单个字符串
 */
export async function encryptPassword(plainText) {
  if (plainText === undefined || plainText === null || plainText === '') {
    return plainText;
  }
  const publicKey = await fetchPublicKey();
  const encryptor = new JSEncrypt();
  encryptor.setPublicKey(publicKey);
  const encrypted = encryptor.encrypt(String(plainText));
  if (!encrypted) {
    throw new Error(ENCRYPT_FAIL_MSG);
  }
  return encrypted;
}

/**
 * 为请求体加密 password / password_confirm 字段
 * @returns {{ payload: object, encrypted: boolean }}
 */
export async function buildEncryptedPasswordPayload(payload = {}) {
  const result = { ...payload };
  const fields = ['password', 'password_confirm'];
  const hasPasswordField = fields.some((f) => result[f] !== undefined && result[f] !== '');

  if (!hasPasswordField) {
    return { payload: result, encrypted: false };
  }

  try {
    for (const field of fields) {
      if (result[field] !== undefined && result[field] !== '') {
        result[field] = await encryptPassword(result[field]);
      }
    }
    result.encrypted = 1;
    return { payload: result, encrypted: true };
  } catch (err) {
    throw err instanceof Error ? err : new Error(ENCRYPT_FAIL_MSG);
  }
}

export { ENCRYPT_FAIL_MSG };
